/**
 * @file: useOrderOcrScan.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CameraShadeImageSource } from '@euler/app/components/media/camera/CameraShade';
import { useIsIpad } from '@euler/functions/responsiveness';
import { VehicleInfoCore } from '@euler/model';
import { VehicleServiceInfo } from '@euler/services';
import { onErrorIgnore, SafeHaptics } from '@euler/utils';
import { isSimulator } from '@euler/utils/device';
import { usePersistFn } from '@euler/utils/hooks';
import { isValidLicensePlateNo } from '@euler/utils/validators';
import { Image, Platform } from 'react-native';
import { useOrderFlowContext } from '../../../OrderFlowContext';
import { useCheckExistingOrder } from './useCheckExistingOrder';
import { useVehicleInfoLoader } from './useVehicleInfoLoader';

export function useOrderOcrScan({ type }: { type: 'vin' | 'plate' }) {
  const {
    navigation,
    orderDetail$,
    ocrImageInfo: { licensePlateNo$, vin$ },
    formController: { form },
    lastVehicleInfo$,
    lastServiceInfo$,
  } = useOrderFlowContext();

  const isIpad = useIsIpad();

  const thisOcrImageInfo$ = type === 'plate' ? licensePlateNo$ : vin$;
  const thatOcrImageInfo$ = type === 'plate' ? vin$ : licensePlateNo$;

  const loadVehicleInfoByVIN = useVehicleInfoLoader();
  const checkExistingOrder = useCheckExistingOrder();

  const onScan = usePersistFn(() => {
    const updateOcrStats = (
      imageUrl: string | undefined,
      vehicleInfo: VehicleInfoCore | undefined,
    ) => {
      // save the image url that is used for recognizing
      thisOcrImageInfo$.next(
        imageUrl ? { imageUrl, timestamp: new Date() } : undefined,
      );

      if (vehicleInfo) {
        // if the result comes from recognizing plate/vin, but the other
        // does not match the current value, just reset the stats info.
        if (
          (type === 'plate' && vehicleInfo.vin !== form.vin.value) ||
          (type === 'vin' &&
            vehicleInfo.licensePlateNo !== form.licensePlateNo.value)
        ) {
          thatOcrImageInfo$.next(undefined);
        }
      }
    };

    const updateResult = (
      value: string,
      serviceInfo: VehicleServiceInfo | undefined,
    ) => {
      const vehicleInfo = serviceInfo?.vehicleInfo;
      if (vehicleInfo) {
        form.licensePlateNo.update(vehicleInfo.licensePlateNo);
        form.isSpecialLicensePlateNo.update(
          !isValidLicensePlateNo(vehicleInfo.licensePlateNo),
        );
        form.vin.update(vehicleInfo.vin);
        form.vehicleInfo.update(vehicleInfo);
      } else if (type === 'plate') {
        form.licensePlateNo.update(value);
        form.isSpecialLicensePlateNo.update(!isValidLicensePlateNo(value));
      } else {
        form.vin.update(value);
        if (lastVehicleInfo$.value?.vin !== value) {
          loadVehicleInfoByVIN(value).catch(onErrorIgnore);
        }
      }

      lastServiceInfo$.next(serviceInfo);

      form.mileage.onBlur();

      void checkExistingOrder(type, value);
    };

    SafeHaptics.selection();

    if (isSimulator()) {
      const asset = Image.resolveAssetSource(CameraShadeImageSource);
      navigation.push(
        type === 'vin' ? '_vinRecognizeResult' : '_plateRecognizeResult',
        {
          imageUri: asset.uri,
          originalImageUri: asset.uri,
          simulatedValue:
            type === 'vin' ? form.vin.value : form.licensePlateNo.value,
          orderDetail$,
          onConfirm: (value, imageUrl, serviceInfo) => {
            updateOcrStats(imageUrl, serviceInfo?.vehicleInfo);
            updateResult(value, serviceInfo);
          },
        },
      );
    } else {
      navigation.push('_vehicleInfoScanner', {
        mode: type,
        modal: Platform.OS === 'ios' && !isIpad,
        orderDetail$,
        onConfirm: (_mode, value, imageUrl, serviceInfo) => {
          updateOcrStats(imageUrl, serviceInfo?.vehicleInfo);
          updateResult(value, serviceInfo);
        },
      });
    }
  });

  return { onScan };
}
